import React, { useState, useCallback, useEffect } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import {
  StepFilters,
  StepFaceQuality,
  StepDeepfake,
  StepLiveness,
  StepFaceOcclusion,
} from '@vlabs/pages/handlers/preconfigured-scenarios/other/steps/index';
import { useSteppedForm, useFaceOcclusionIsAvailable } from '@vlabs/shared/hooks';
import { selectAppServices } from '@vlabs/shared/selectors/appSelectors';
import { Stepper, Modal } from '@vlabs/uikit';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import st from './CreateVerifier.module.sass';
import { defaultVerifier, getCompatibleVerifier, faceOcclusionDefaultValues } from './defaultVerifier';
import { CreatedVerifierForm } from './forms/index';
import {
  StepNameOfVerifier,
  StepSelectionOfAttribute,
  StepSaveFaceAndAttributes,
} from './steps/index';
import { Page, PageContent, PageHeader } from '@vlabs/monokit/shared/ui/page';

const steps = [
  StepNameOfVerifier,
  StepSelectionOfAttribute,
  StepLiveness,
  StepDeepfake,
  StepFaceQuality,
  // StepFaceOcclusion, // пока добавляем отдельно только для версии lp 5.84.0
  StepFilters,
  StepSaveFaceAndAttributes,
];

function CreateVerifier({ lpVersion }) {
  const { t } = useTranslation();
  const history = useHistory();

  const [createdModalIsOpen, setCreatedModalIsOpen] = useState(false);
  const [createdVerifierId, setCreatedVerifierId] = useState();
  const [createdVerifierName, setCreatedVerifierName] = useState();
  // FIXME: getCompatibleVerifier(lpVersion) костыли для проверки версии, для версии платформы выше 5.101 добавляем поле image_modification
  const [defaultValues, setDefaultValues] = useState(defaultVerifier);

  const onCloseCreatedModal = () => {
    setCreatedModalIsOpen(false);
    history.goBack();
  };

  const form = useForm({
    defaultValues,
    mode: 'onChange',
  });
  const { getValues, reset, handleSubmit } = form;

  // FIXME временное решение, в будущем добавлять StepFaceOcclusion по умолчанию
  const isFaceOcclusionAvailable = useFaceOcclusionIsAvailable();

  useEffect(() => {
    if (!lpVersion) return;
    const verifier = getCompatibleVerifier(lpVersion);
    setDefaultValues(verifier);
    reset(verifier);
  }, [lpVersion]);

  useEffect(() => {
    if (!isFaceOcclusionAvailable || steps.includes(StepFaceOcclusion)) return;
    steps.splice(5, 0, StepFaceOcclusion); // шаг 6 после StepFaceQualty

    const updated = {
      ...getValues(),
      ...faceOcclusionDefaultValues,
    };

    setDefaultValues(updated);
    reset(updated);
  }, [isFaceOcclusionAvailable]);

  const onSubmit = useCallback(async (values) => {
    const { verifier_id: verifierId } = await apiContainer.lunaClient.verifiers.create(values);
    return verifierId;
  }, []);

  const onSaveVerifier = useCallback(async (values) => {
    setCreatedVerifierId(await onSubmit(values));
    setCreatedVerifierName(values?.description);
    setCreatedModalIsOpen(true);
  }, [onSubmit, setCreatedModalIsOpen]);

  const canGoNextStep = useCallback(() => {
    return !(document.querySelector('.ReactModal__Content--after-open'));
  }, []);

  const stepper = useSteppedForm({
    form,
    canGoNextStep,
    steps,
    onSubmit: onSaveVerifier,
    buttonsClass: st.StepperButtons,
  });

  const context = {
    ...form,
  };

  return (
    <FormProvider {...context}>
      <form onSubmit={handleSubmit(onSaveVerifier)} style={{ flex: 1 }}>
        <Modal
          appElement={document.getElementById('root')}
          data-testid="modal.saveVerifier"
          isOpen={createdModalIsOpen}
          onRequestClose={onCloseCreatedModal}
          size="small"
        >
          <CreatedVerifierForm
            verifierId={createdVerifierId}
            verifierName={createdVerifierName}
          />
        </Modal>
        <Page>
          <PageContent>
            <PageHeader title={t('verifiers:form.createFormTitle')} />
            <Stepper {...stepper} className={st.StepperWrapper}>
              {steps.map((Step) => <Step key={Step.name} />)}
            </Stepper>
          </PageContent>
        </Page>
      </form>
    </FormProvider>

  );
}

export default connect((state) => ({
  lpVersion: selectAppServices(state)?.lunaPlatform?.versionNum,
}))(CreateVerifier);
